import classNames from 'classnames';
import type { MouseEvent, ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { fadeIn } from '#src/atoms/animations/keyframes.ts';
import { Portal } from '#src/atoms/layout/Portal/index.tsx';
import { Horizontal } from '#src/atoms/layout/flex/index.tsx';
import { Backdrop } from '#src/atoms/loader/Backdrop/index.tsx';
import { useCurrentUserContext } from '#src/contexts/currentUser/index.ts';
import type {
  Highlight,
  OnboardingHighlightName,
} from '#src/contexts/onboardingHighlight/index.tsx';
import { useOnboardingHighLightContext } from '#src/contexts/onboardingHighlight/index.tsx';
import { useResizeObserver } from '#src/hooks/ui/useResizeObserver.ts';
import { useBodyLock } from '#src/hooks/useBodyLock.ts';
import { useToggle } from '#src/hooks/useToggle.ts';
import { waitMs } from '#src/lib/animation/waitMs.ts';
import { scrollIntoViewAndWait } from '#src/lib/scrollIntoViewAndWait.ts';
import { isFullyInViewport } from '#src/lib/ui/isFullyInViewport.ts';
import { theme } from '#src/style/theme.tsx';

const ABOVE_MODAL_Z_INDEX = theme.zIndex.modal + 1;

const StyledBackdrop = styled(Backdrop)<{ blur?: number }>`
  z-index: var(--highlight-overlay-z-index, ${theme.zIndex.backdrop - 1});
  backdrop-filter: ${({ blur }) => `blur(${blur || 5}px)`};
  &.aboveModal {
    --highlight-overlay-z-index: ${ABOVE_MODAL_Z_INDEX};
  }
`;

const AboveBackdropDiv = styled.div`
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: calc(
    var(--highlight-overlay-z-index, ${theme.zIndex.backdrop - 1}) + 1
  );
  height: 100%;
  width: 100%;
  &.aboveModal {
    --highlight-overlay-z-index: ${ABOVE_MODAL_Z_INDEX};
  }
`;
const AbsolutelyPositionedDiv = styled(Horizontal).attrs({ center: true })`
  position: absolute;
  & > * {
    flex: 1;
  }
`;

const ExtraWrapper = styled.div`
  &.withDelay {
    opacity: 0;
    animation: ${fadeIn} 0.6s 1s ease-in-out forwards;
  }
`;

const getDimensionsWithPadding = (highlight: Highlight) => {
  const { measureCallback, config } = highlight;
  const domRect = measureCallback?.();

  if (!domRect) {
    return {
      left: 0,
      top: 0,
      right: 0,
      bottom: 0,
      ready: false,
    };
  }

  const { padding = 0, circle } = config || {};
  const { left, top, right, bottom, width, height } = domRect;
  const isReady = !!height;

  if (circle) {
    const radius = Math.max(width / 2, height / 2) + padding;
    const horizontalCenter = left + width / 2;
    const verticalCenter = top + height / 2;

    return {
      left: horizontalCenter - radius,
      top: verticalCenter - radius,
      right: horizontalCenter + radius,
      bottom: verticalCenter + radius,
      ready: isReady,
    };
  }

  return {
    left: left - padding,
    top: top - padding,
    right: right + padding,
    bottom: bottom + padding,
    ready: isReady,
  };
};

type Props = {
  highlightNames: OnboardingHighlightName[];
  blur?: number;
  onClose?: () => void;
  disableBackdropClose?: boolean;
  withDelay?: boolean;
  stayOpen?: boolean;
  scrollBlock?: ScrollLogicalPosition;
  aboveModal?: boolean;
  extras?: {
    nextTo: OnboardingHighlightName;
    render: ({
      left,
      top,
      right,
      bottom,
      onClose,
      ready,
    }: {
      left: number;
      top: number;
      right: number;
      bottom: number;
      ready: boolean;
      onClose: () => void;
    }) => ReactNode;
  }[];
};

const ACTION_DELAY_MS = 600;

export const HighlightOverlay = ({
  highlightNames,
  blur,
  onClose: onCloseFromProps,
  disableBackdropClose,
  withDelay,
  extras,
  stayOpen,
  scrollBlock = 'start',
  aboveModal,
}: Props) => {
  const [show, setShow] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const [, forceRerender] = useToggle(false);
  const { currentUser } = useCurrentUserContext();
  const { highlights, setActiveHighlights } = useOnboardingHighLightContext();
  const currentHighlights = highlights.filter(highlight =>
    highlightNames.includes(highlight.name)
  );

  // Lock body when the overlay is shown
  useBodyLock(show);

  useResizeObserver(ref, () => {
    forceRerender();
  });

  const { refToScroll } = currentHighlights.find(h => h.refToScroll) || {};

  useEffect(() => {
    setActiveHighlights(highlightNames);
    return () => {
      setActiveHighlights([]);
    };
  }, [setActiveHighlights, highlightNames]);

  const currentHighlightRegistered =
    currentHighlights.length === highlightNames.length;
  useEffect(() => {
    if (!currentHighlightRegistered) {
      return;
    }
    const maybeScrollToHighlightAndShow = async () => {
      if (refToScroll?.current) {
        if (!isFullyInViewport(refToScroll.current)) {
          if (withDelay) {
            await waitMs(ACTION_DELAY_MS);
          }
          //after waiting the ref.current can be gone
          if (refToScroll?.current) {
            await scrollIntoViewAndWait(refToScroll.current, {
              block: scrollBlock,
              inline: 'start',
              behavior: 'smooth',
            });
          }
        }
      }
      setShow(true);
    };
    maybeScrollToHighlightAndShow();
  }, [
    refToScroll,
    withDelay,
    setActiveHighlights,
    currentHighlightRegistered,
    scrollBlock,
  ]);

  const onClose = () => {
    onCloseFromProps?.();

    if (!stayOpen) {
      setShow(false);
    }
  };

  if (
    show &&
    !currentHighlights?.find(
      highlight => getDimensionsWithPadding(highlight).ready
    )
  ) {
    setShow(false);
  }

  if (!currentUser || !show) {
    return null;
  }

  return (
    <Portal id="onboarding-overlay">
      <StyledBackdrop
        ref={ref}
        blur={blur}
        delay={withDelay ? ACTION_DELAY_MS : 0}
        className={classNames({ aboveModal })}
      />
      <AboveBackdropDiv
        className={classNames({ aboveModal })}
        onClick={
          disableBackdropClose
            ? undefined
            : e => {
                e.stopPropagation();
                onClose();
              }
        }
      >
        {currentHighlights.map(highlight => {
          const { left, top, right, bottom } =
            getDimensionsWithPadding(highlight);

          const { config, children } = highlight;
          const {
            border,
            borderColor = 'var(--c-white)',
            borderRadius,
            circle,
            onClick,
            padding,
            filter,
          } = config || {};

          const isButton = !!onClick || !!onCloseFromProps || !stayOpen;
          return (
            <AbsolutelyPositionedDiv
              key={highlight.name}
              onClickCapture={
                isButton
                  ? e => {
                      e.stopPropagation();
                      e.preventDefault();
                      onClick?.(e as unknown as MouseEvent<HTMLButtonElement>);
                      onClose();
                    }
                  : undefined
              }
              role={isButton ? 'button' : undefined}
              aria-label={`Click ${highlight.name}`}
              style={{
                top,
                left,
                width: right - left,
                height: circle ? right - left : bottom - top,
                borderRadius: circle ? '50%' : borderRadius,
                cursor: isButton ? 'pointer' : undefined,
                border: border ? `2px solid ${borderColor}` : 'none',
                padding: `${padding}px`,
                filter,
              }}
              className={classNames({
                withDelay,
              })}
            >
              {children}
            </AbsolutelyPositionedDiv>
          );
        })}
        {extras?.map(({ nextTo, render }, index) => {
          const nextToHighlight = currentHighlights.find(
            h => h.name === nextTo
          );

          if (!nextToHighlight) return null;
          return (
            // oxlint-disable-next-line react/no-array-index-key
            <ExtraWrapper key={index} className={classNames({ withDelay })}>
              {render({
                onClose,
                ...getDimensionsWithPadding(nextToHighlight),
              })}
            </ExtraWrapper>
          );
        })}
      </AboveBackdropDiv>
    </Portal>
  );
};
